import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { playerAvatarImgClassName } from '../../../utils/avatarClassNames';

const PROJECTION_EDITOR_EMAIL = 'mhoelzel23@gmail.com';

function toNumber(value) {
	const parsed = parseFloat(value);
	return Number.isNaN(parsed) ? 0 : parsed;
}

function round2(value) {
	return Math.round(toNumber(value) * 100) / 100;
}

function formatTeamInteger(value) {
	return Math.round(toNumber(value)).toLocaleString();
}

const TEAM_INTEGER_FIELDS = [
	'proj_pass_attempts',
	'proj_passing_yards',
	'proj_passing_touchdowns',
	'proj_rush_attempts',
	'proj_rushing_yards',
	'proj_rushing_touchdowns'
];

function formatProjectionNumber(value) {
	const numeric = toNumber(value);
	return numeric.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

function calculatePlayer(player, team) {
	const position = player.position;
	const passAttempts = toNumber(team.proj_pass_attempts);
	const passingTouchdowns = toNumber(team.proj_passing_touchdowns);
	const rushAttempts = toNumber(team.proj_rush_attempts);
	const rushingTouchdowns = toNumber(team.proj_rushing_touchdowns);

	if (position === 'QB') {
		const projPassAttempts = round2(passAttempts * (toNumber(player.proj_passing_share_pct) / 100));
		const projRushAttempts = round2(rushAttempts * (toNumber(player.proj_carry_share_pct) / 100));
		return {
			...player,
			proj_pass_attempts: projPassAttempts,
			proj_passing_yards: round2(projPassAttempts * toNumber(player.proj_passing_yards_per_attempt)),
			proj_passing_touchdowns: round2(passingTouchdowns * (toNumber(player.proj_passing_share_pct) / 100)),
			proj_rush_attempts: projRushAttempts,
			proj_rushing_yards: round2(projRushAttempts * toNumber(player.proj_yards_per_carry)),
			proj_rushing_touchdowns: round2(rushingTouchdowns * (toNumber(player.proj_carry_share_pct) / 100))
		};
	}

	if (position === 'RB') {
		const projTargets = round2(passAttempts * (toNumber(player.proj_target_share_pct) / 100));
		const projReceptions = round2(projTargets * (toNumber(player.proj_catch_rate_pct) / 100));
		const projRushAttempts = round2(rushAttempts * (toNumber(player.proj_carry_share_pct) / 100));
		return {
			...player,
			proj_targets: projTargets,
			proj_receptions: projReceptions,
			proj_receiving_yards: round2(projReceptions * toNumber(player.proj_yards_per_reception)),
			proj_rush_attempts: projRushAttempts,
			proj_rushing_yards: round2(projRushAttempts * toNumber(player.proj_yards_per_carry)),
			proj_receiving_touchdowns: round2(passingTouchdowns * (toNumber(player.proj_target_share_pct) / 100)),
			proj_rushing_touchdowns: round2(rushingTouchdowns * (toNumber(player.proj_carry_share_pct) / 100))
		};
	}

	const projTargets = round2(passAttempts * (toNumber(player.proj_target_share_pct) / 100));
	const projReceptions = round2(projTargets * (toNumber(player.proj_catch_rate_pct) / 100));
	return {
		...player,
		proj_targets: projTargets,
		proj_receptions: projReceptions,
		proj_receiving_yards: round2(projReceptions * toNumber(player.proj_yards_per_reception)),
		proj_receiving_touchdowns: round2(passingTouchdowns * (toNumber(player.proj_target_share_pct) / 100))
	};
}

export default function ProjectionTeamSheet({
	get_projection_team_endpoint,
	update_projection_team_endpoint
}) {
	const { id } = useParams();
	const { user } = useAuth();
	const canEditProjections = user?.email === PROJECTION_EDITOR_EMAIL;
	const [
		loading,
		setLoading
	] = useState(true);
	const [
		saving,
		setSaving
	] = useState(false);
	const [
		error,
		setError
	] = useState('');
	const [
		isEditing,
		setIsEditing
	] = useState(false);
	const isEditingMode = canEditProjections && isEditing;
	const [
		originalState,
		setOriginalState
	] = useState({
		team: null,
		players: []
	});
	const [
		draftState,
		setDraftState
	] = useState({
		team: null,
		players: []
	});

	const fetchProjectionTeam = async () => {
		setLoading(true);
		setError('');
		try {
			const response = await fetch(
				get_projection_team_endpoint.replace('team_id', id)
			);
			const result = await response.json();
			if (!result.success) throw new Error(result.message || 'Failed to fetch');

			const initial = {
				team: result.data.team,
				players: result.data.players
			};
			setOriginalState(initial);
			setDraftState(initial);
		} catch (fetchError) {
			setError(fetchError.message);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		fetchProjectionTeam();
	}, [
		id
	]);

	useEffect(() => {
		if (!canEditProjections) {
			setIsEditing(false);
		}
	}, [
		canEditProjections
	]);

	const calculatedPlayers = useMemo(
		() =>
			(draftState.players || []).map((player) =>
				calculatePlayer(player, draftState.team || {})
			),
		[
			draftState
		]
	);

	const handleTeamChange = (field, value) => {
		const normalizedValue = TEAM_INTEGER_FIELDS.includes(field)
			? value.replace(/[^0-9]/g, '')
			: value;

		setDraftState((prev) => ({
			...prev,
			team: {
				...prev.team,
				[field]: normalizedValue
			}
		}));
	};

	const handlePlayerChange = (playerId, field, value) => {
		setDraftState((prev) => ({
			...prev,
			players: prev.players.map((player) =>
				player.id === playerId
					? {
							...player,
							[field]: value
					  }
					: player
			)
		}));
	};

	const cancelEdit = () => {
		setDraftState(originalState);
		setIsEditing(false);
		setError('');
	};

	const saveChanges = async () => {
		setSaving(true);
		setError('');
		try {
			const csrfTokenElement = document.querySelector('[name="csrf-token"]');
			const csrfToken = csrfTokenElement ? csrfTokenElement.content : null;
			const payload = {
				team: {
					proj_pass_attempts: toNumber(draftState.team.proj_pass_attempts),
					proj_passing_yards: toNumber(draftState.team.proj_passing_yards),
					proj_passing_touchdowns: toNumber(draftState.team.proj_passing_touchdowns),
					proj_rush_attempts: toNumber(draftState.team.proj_rush_attempts),
					proj_rushing_yards: toNumber(draftState.team.proj_rushing_yards),
					proj_rushing_touchdowns: toNumber(draftState.team.proj_rushing_touchdowns)
				},
				players: draftState.players.map((player) => ({
					id: player.id,
					position: player.position,
					depth_chart_position: toNumber(player.depth_chart_position),
					proj_passing_share_pct: toNumber(player.proj_passing_share_pct),
					proj_target_share_pct: toNumber(player.proj_target_share_pct),
					proj_catch_rate_pct: toNumber(player.proj_catch_rate_pct),
					proj_yards_per_reception: toNumber(player.proj_yards_per_reception),
					proj_passing_yards_per_attempt: toNumber(player.proj_passing_yards_per_attempt),
					proj_carry_share_pct: toNumber(player.proj_carry_share_pct),
					proj_yards_per_carry: toNumber(player.proj_yards_per_carry),
					proj_rushing_touchdowns: toNumber(player.proj_rushing_touchdowns)
				}))
			};

			const response = await fetch(
				update_projection_team_endpoint.replace('team_id', id),
				{
					method: 'PUT',
					headers: {
						'Content-Type': 'application/json',
						'X-CSRF-Token': csrfToken
					},
					body: JSON.stringify(payload)
				}
			);
			const result = await response.json();
			if (!result.success) throw new Error(result.message || 'Failed to save');

			const updated = {
				team: result.data.team,
				players: result.data.players
			};
			setOriginalState(updated);
			setDraftState(updated);
			setIsEditing(false);
		} catch (saveError) {
			setError(saveError.message);
		} finally {
			setSaving(false);
		}
	};

	const qbs = calculatedPlayers.filter((player) => player.position === 'QB');
	const rbs = calculatedPlayers.filter((player) => player.position === 'RB');
	const wrs = calculatedPlayers.filter((player) => player.position === 'WR');
	const tes = calculatedPlayers.filter((player) => player.position === 'TE');

	const renderEditableNumber = (player, field, options = {}) => {
		const {
			step = '0.01',
			width = 'w-20',
			inputMode = step === '1' ? 'numeric' : 'decimal'
		} = options;
		if (!isEditingMode) return formatProjectionNumber(player[field]);

		return (
			<input
				type="text"
				inputMode={inputMode}
				step={step}
				value={player[field] || 0}
				onChange={(event) => handlePlayerChange(player.id, field, event.target.value)}
				className={`${width} border border-gray-300 dark:border-dark-border rounded px-1 py-1 text-right bg-white dark:bg-dark-card text-gray-800 dark:text-dark-text`}
			/>
		);
	};

	const renderSectionTable = (title, headers, rows) => (
		<div className="mb-6">
			<h3 className="text-lg font-semibold text-gray-800 dark:text-dark-text mb-2">{title}</h3>
			<div className="overflow-x-auto">
				<table className="min-w-full text-xs md:text-sm text-gray-800 dark:text-dark-text">
					<thead>
						<tr className="border-b border-gray-200 dark:border-dark-border">
							{headers.map((header, index) => (
								<th
									key={header}
									className={`${index === 0 ? 'text-left' : 'text-right'} py-2 px-2`}
								>
									{header}
								</th>
							))}
						</tr>
					</thead>
					<tbody>{rows}</tbody>
				</table>
			</div>
		</div>
	);

	if (loading) return <div className="text-gray-600 dark:text-gray-300">Loading projections...</div>;
	if (!draftState.team) return <div className="text-red-600">Unable to load team.</div>;

	return (
		<div className="space-y-4 text-gray-800 dark:text-dark-text">
			<div className="p-6 lg:p-8 border border-gray-300 dark:border-dark-border rounded-xl shadow-lg bg-white dark:bg-dark-card">
				<div className="flex items-center justify-between flex-wrap gap-2 mb-4">
					<div className="flex items-center gap-3">
						<div
							style={{
								width: '50px',
								height: '50px',
								overflow: 'hidden'
							}}
						>
							<img
								className={playerAvatarImgClassName}
								src={`${draftState.team.image_url}?w=50&h=50`}
								alt={draftState.team.name}
								style={{
									width: '50px',
									height: '50px',
									minWidth: '50px',
									minHeight: '50px'
								}}
							/>
						</div>
						<h2 className="text-2xl font-semibold text-gray-800 dark:text-dark-text">{draftState.team.name} Projections</h2>
					</div>
					{canEditProjections && (
					<div className="flex gap-2">
						{!isEditing && (
							<button
								onClick={() => setIsEditing(true)}
								className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700"
							>
								Edit
							</button>
						)}
						{isEditing && (
							<>
								<button
									onClick={cancelEdit}
									className="px-4 py-2 rounded-lg border border-gray-300 dark:border-dark-border text-gray-700 dark:text-dark-text bg-white dark:bg-dark-card"
								>
									Cancel
								</button>
								<button
									onClick={saveChanges}
									disabled={saving}
									className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
								>
									{saving ? 'Saving...' : 'Save'}
								</button>
							</>
						)}
					</div>
					)}
				</div>

				<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
					{[
						[
							'proj_pass_attempts',
							'Proj Pass Attempts'
						],
						[
							'proj_passing_yards',
							'Proj Passing Yards'
						],
						[
							'proj_passing_touchdowns',
							'Proj Passing TD'
						],
						[
							'proj_rush_attempts',
							'Proj Rush Attempts'
						],
						[
							'proj_rushing_yards',
							'Proj Rushing Yards'
						],
						[
							'proj_rushing_touchdowns',
							'Proj Rushing TD'
						]
					].map(([field, label]) => (
						<div key={field} className="rounded-lg border border-gray-200 dark:border-dark-border p-3 bg-gray-50 dark:bg-dark-bg">
							<div className="text-xs uppercase text-gray-500 dark:text-gray-300 mb-1">{label}</div>
							{isEditingMode ? (
								<input
									type="text"
									inputMode="numeric"
									step="0.01"
									value={Math.round(toNumber(draftState.team[field] || 0))}
									onChange={(event) => handleTeamChange(field, event.target.value)}
									className="w-full border border-gray-300 dark:border-dark-border rounded px-2 py-1 text-gray-800 dark:text-dark-text bg-white dark:bg-dark-card"
								/>
							) : (
								<div className="text-gray-800 dark:text-dark-text font-semibold">{formatTeamInteger(draftState.team[field])}</div>
							)}
						</div>
					))}
				</div>

				{error && <div className="text-red-600 text-sm mb-3">{error}</div>}

				{renderSectionTable(
					'Quarterbacks',
					[
						'Player',
						'Pos',
						'Depth',
						'Passing Share %',
						'Pass Y/A',
						'Carry Share %',
						'YPC',
						'Proj Pass Att',
						'Proj Pass Yds',
						'Proj Pass TD',
						'Proj Rush Att',
						'Proj Rush Yds',
						'Proj Rush TD'
					],
					qbs.map((player) => (
						<tr key={`${player.position}-${player.id}`} className="border-b border-gray-100 dark:border-dark-border">
							<td className="py-2 px-2 text-left">{player.name}</td>
							<td className="py-2 px-2 text-right">{player.position}</td>
							<td className="py-2 px-2 text-right">{renderEditableNumber(player, 'depth_chart_position', { step: '1', width: 'w-16' })}</td>
							<td className="py-2 px-2 text-right">{renderEditableNumber(player, 'proj_passing_share_pct')}</td>
							<td className="py-2 px-2 text-right">{renderEditableNumber(player, 'proj_passing_yards_per_attempt')}</td>
							<td className="py-2 px-2 text-right">{renderEditableNumber(player, 'proj_carry_share_pct')}</td>
							<td className="py-2 px-2 text-right">{renderEditableNumber(player, 'proj_yards_per_carry')}</td>
							<td className="py-2 px-2 text-right">{formatProjectionNumber(player.proj_pass_attempts)}</td>
							<td className="py-2 px-2 text-right">{formatProjectionNumber(player.proj_passing_yards)}</td>
							<td className="py-2 px-2 text-right">{formatProjectionNumber(player.proj_passing_touchdowns)}</td>
							<td className="py-2 px-2 text-right">{formatProjectionNumber(player.proj_rush_attempts)}</td>
							<td className="py-2 px-2 text-right">{formatProjectionNumber(player.proj_rushing_yards)}</td>
							<td className="py-2 px-2 text-right">{formatProjectionNumber(player.proj_rushing_touchdowns)}</td>
						</tr>
					))
				)}

				{renderSectionTable(
					'Running Backs',
					[
						'Player',
						'Pos',
						'Depth',
						'Tgt Share %',
						'Catch %',
						'YPR',
						'Carry Share %',
						'YPC',
						'Proj Rush Att',
						'Proj Rush Yds',
						'Proj Rush TD',
						'Proj Tgt',
						'Proj Rec',
						'Proj Rec Yds',
						'Proj Rec TD'
					],
					rbs.map((player) => (
						<tr key={`${player.position}-${player.id}`} className="border-b border-gray-100 dark:border-dark-border">
							<td className="py-2 px-2 text-left">{player.name}</td>
							<td className="py-2 px-2 text-right">{player.position}</td>
							<td className="py-2 px-2 text-right">{renderEditableNumber(player, 'depth_chart_position', { step: '1', width: 'w-16' })}</td>
							<td className="py-2 px-2 text-right">{renderEditableNumber(player, 'proj_target_share_pct')}</td>
							<td className="py-2 px-2 text-right">{renderEditableNumber(player, 'proj_catch_rate_pct')}</td>
							<td className="py-2 px-2 text-right">{renderEditableNumber(player, 'proj_yards_per_reception')}</td>
							<td className="py-2 px-2 text-right">{renderEditableNumber(player, 'proj_carry_share_pct')}</td>
							<td className="py-2 px-2 text-right">{renderEditableNumber(player, 'proj_yards_per_carry')}</td>
							<td className="py-2 px-2 text-right">{formatProjectionNumber(player.proj_rush_attempts)}</td>
							<td className="py-2 px-2 text-right">{formatProjectionNumber(player.proj_rushing_yards)}</td>
							<td className="py-2 px-2 text-right">{formatProjectionNumber(player.proj_rushing_touchdowns)}</td>
							<td className="py-2 px-2 text-right">{formatProjectionNumber(player.proj_targets)}</td>
							<td className="py-2 px-2 text-right">{formatProjectionNumber(player.proj_receptions)}</td>
							<td className="py-2 px-2 text-right">{formatProjectionNumber(player.proj_receiving_yards)}</td>
							<td className="py-2 px-2 text-right">{formatProjectionNumber(player.proj_receiving_touchdowns)}</td>
						</tr>
					))
				)}

				{renderSectionTable(
					'Wide Receivers',
					[
						'Player',
						'Pos',
						'Depth',
						'Tgt Share %',
						'Catch %',
						'YPR',
						'Proj Tgt',
						'Proj Rec',
						'Proj Rec Yds',
						'Proj Rec TD'
					],
					wrs.map((player) => (
						<tr key={`${player.position}-${player.id}`} className="border-b border-gray-100 dark:border-dark-border">
							<td className="py-2 px-2 text-left">{player.name}</td>
							<td className="py-2 px-2 text-right">{player.position}</td>
							<td className="py-2 px-2 text-right">{renderEditableNumber(player, 'depth_chart_position', { step: '1', width: 'w-16' })}</td>
							<td className="py-2 px-2 text-right">{renderEditableNumber(player, 'proj_target_share_pct')}</td>
							<td className="py-2 px-2 text-right">{renderEditableNumber(player, 'proj_catch_rate_pct')}</td>
							<td className="py-2 px-2 text-right">{renderEditableNumber(player, 'proj_yards_per_reception')}</td>
							<td className="py-2 px-2 text-right">{formatProjectionNumber(player.proj_targets)}</td>
							<td className="py-2 px-2 text-right">{formatProjectionNumber(player.proj_receptions)}</td>
							<td className="py-2 px-2 text-right">{formatProjectionNumber(player.proj_receiving_yards)}</td>
							<td className="py-2 px-2 text-right">{formatProjectionNumber(player.proj_receiving_touchdowns)}</td>
						</tr>
					))
				)}

				{renderSectionTable(
					'Tight Ends',
					[
						'Player',
						'Pos',
						'Depth',
						'Tgt Share %',
						'Catch %',
						'YPR',
						'Proj Tgt',
						'Proj Rec',
						'Proj Rec Yds',
						'Proj Rec TD'
					],
					tes.map((player) => (
						<tr key={`${player.position}-${player.id}`} className="border-b border-gray-100 dark:border-dark-border">
							<td className="py-2 px-2 text-left">{player.name}</td>
							<td className="py-2 px-2 text-right">{player.position}</td>
							<td className="py-2 px-2 text-right">{renderEditableNumber(player, 'depth_chart_position', { step: '1', width: 'w-16' })}</td>
							<td className="py-2 px-2 text-right">{renderEditableNumber(player, 'proj_target_share_pct')}</td>
							<td className="py-2 px-2 text-right">{renderEditableNumber(player, 'proj_catch_rate_pct')}</td>
							<td className="py-2 px-2 text-right">{renderEditableNumber(player, 'proj_yards_per_reception')}</td>
							<td className="py-2 px-2 text-right">{formatProjectionNumber(player.proj_targets)}</td>
							<td className="py-2 px-2 text-right">{formatProjectionNumber(player.proj_receptions)}</td>
							<td className="py-2 px-2 text-right">{formatProjectionNumber(player.proj_receiving_yards)}</td>
							<td className="py-2 px-2 text-right">{formatProjectionNumber(player.proj_receiving_touchdowns)}</td>
						</tr>
					))
				)}
			</div>
		</div>
	);
}
